import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Folder, FileText, Upload, Download, Eye, MoreHorizontal, Plus, Loader2, Pen, CheckCircle2, XCircle, Shield, Users, Lock } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useMatterStages, useDocuments, useMatterPartyNames, getPartyName } from "@/hooks/use-supabase-queries";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { SignatureVerification } from "@/components/documents/SignatureVerification";
import { DocumentUploadDialog } from "@/components/documents/DocumentUploadDialog";
import { toast } from "sonner";
import { DOCUMENT_VISIBILITY } from "@/lib/constants";
import { SignatureDialog } from "@/components/documents/SignatureDialog";
import { canDeleteDocument } from "@/lib/documents";
import { useDeleteDocument } from "@/hooks/use-supabase-queries";

const statusBadgeConfig: Record<string, string> = {
  verified: "bg-primary/10 text-primary",
  signed: "bg-primary/10 text-primary",
  pending: "bg-accent text-accent-foreground",
  rejected: "bg-destructive/10 text-destructive",
};

const visibilityIcons: Record<string, typeof Lock> = {
  'lawyer-only': Lock,
  'lawyer-agent': Shield,
  'all-parties': Users,
};

function formatFileSize(bytes: number | null) {
  if (!bytes) return '';
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

interface MatterDocumentsProps {
  matterId: string;
}

export function MatterDocuments({ matterId }: MatterDocumentsProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: stages, isLoading: stagesLoading } = useMatterStages(matterId);
  const { data: documents, isLoading: docsLoading } = useDocuments({ matter_id: matterId });
  const { data: partyNames } = useMatterPartyNames(matterId);
  const deleteDocument = useDeleteDocument();

  const [signingDoc, setSigningDoc] = useState<{ id: string; name: string; type: string } | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadStageId, setUploadStageId] = useState<string | undefined>();
  const [rejectDoc, setRejectDoc] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  if (stagesLoading || docsLoading) {
    return <Card><CardContent className="p-8 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></CardContent></Card>;
  }

  const getDocsForStage = (stageId: string) => documents?.filter(d => d.stage_id === stageId) || [];
  const unstagedDocs = documents?.filter(d => !d.stage_id) || [];

  const handleView = async (doc: any) => {
    const { data, error } = await supabase.storage.from('documents').createSignedUrl(doc.file_path, 3600);
    if (error) { toast.error("Could not generate preview URL"); return; }
    window.open(data.signedUrl, '_blank');
  };

  const handleDownload = async (doc: any) => {
    const { data, error } = await supabase.storage.from('documents').download(doc.file_path);
    if (error) { toast.error("Download failed"); return; }
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url; a.download = doc.name + (doc.file_type ? `.${doc.file_type}` : '');
    a.click(); URL.revokeObjectURL(url);
  };

  const handleVerify = async (doc: any) => {
    const { error } = await supabase.from('documents').update({
      status: 'verified', verified_by: user?.id, verified_at: new Date().toISOString(),
    }).eq('id', doc.id);
    if (error) { toast.error("Failed to verify"); return; }
    // Notify uploader
    if (doc.uploaded_by !== user?.id) {
      await supabase.functions.invoke('create-notification', {
        body: {
          user_id: doc.uploaded_by, title: 'Document Verified',
          message: `Your document "${doc.name}" has been verified.`, type: 'document',
        },
      });
    }
    queryClient.invalidateQueries({ queryKey: ['documents'] });
    toast.success("Document verified");
  };

  const handleReject = async () => {
    if (!rejectDoc) return;
    const doc = documents?.find(d => d.id === rejectDoc);
    const { error } = await supabase.from('documents').update({ status: 'rejected' }).eq('id', rejectDoc);
    if (error) { toast.error("Failed to reject"); return; }
    if (doc && doc.uploaded_by !== user?.id) {
      await supabase.functions.invoke('create-notification', {
        body: {
          user_id: doc.uploaded_by, title: 'Document Rejected',
          message: `Your document "${doc.name}" was rejected. Reason: ${rejectReason || 'Not specified'}`, type: 'document',
        },
      });
    }
    queryClient.invalidateQueries({ queryKey: ['documents'] });
    toast.success("Document rejected");
    setRejectDoc(null); setRejectReason("");
  };

  const handleVisibilityChange = async (docId: string, visibility: string) => {
    const { error } = await supabase.from('documents').update({ visibility }).eq('id', docId);
    if (error) { toast.error("Failed to update visibility"); return; }
    // If shared with client, notify
    if (visibility === 'all-parties') {
      const { data: matter } = await supabase.from('matters').select('client_id').eq('id', matterId).single();
      const doc = documents?.find(d => d.id === docId);
      if (matter?.client_id && doc) {
        await supabase.functions.invoke('create-notification', {
          body: {
            user_id: matter.client_id, title: 'Document Shared',
            message: `"${doc.name}" has been shared with you.`, type: 'document',
            link_to: `/client-transaction/${matterId}`,
          },
        });
      }
    }
    queryClient.invalidateQueries({ queryKey: ['documents'] });
    toast.success("Visibility updated");
  };

  const handleDeleteDocument = async (doc: any) => {
    try {
      await deleteDocument.mutateAsync(doc);
      toast.success("Document deleted");
    } catch (error) {
      toast.error("Could not delete document", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    }
  };

  const renderDocRow = (doc: any) => {
    const VisIcon = visibilityIcons[doc.visibility || 'lawyer-only'] || Lock;
    return (
      <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="p-2 rounded bg-background"><FileText className="h-4 w-4 text-primary" /></div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-sm truncate">{doc.name}</p>
              <Badge className={statusBadgeConfig[doc.status || 'pending'] || statusBadgeConfig.pending} variant="secondary">
                {doc.status || 'pending'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {formatFileSize(doc.file_size)} • {format(new Date(doc.created_at), 'MMM d, yyyy')} • {getPartyName(partyNames, doc.uploaded_by)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleView(doc)} aria-label="View document"><Eye className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownload(doc)} aria-label="Download document"><Download className="h-4 w-4" /></Button>
          {doc.status !== 'signed' && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSigningDoc({ id: doc.id, name: doc.name, type: doc.category || 'Document' })} aria-label="Sign document">
              <Pen className="h-4 w-4" />
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Document options"><MoreHorizontal className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-popover border-border">
              {doc.status === 'pending' && (
                <>
                  <DropdownMenuItem onClick={() => handleVerify(doc)}>
                    <CheckCircle2 className="h-4 w-4 mr-2 text-primary" />Verify Document
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setRejectDoc(doc.id); setRejectReason(""); }}>
                    <XCircle className="h-4 w-4 mr-2 text-destructive" />Reject Document
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem className="text-xs text-muted-foreground pointer-events-none">
                <VisIcon className="h-3.5 w-3.5 mr-2" />
                {DOCUMENT_VISIBILITY[doc.visibility as keyof typeof DOCUMENT_VISIBILITY] || 'Lawyer Only'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleVisibilityChange(doc.id, 'lawyer-only')}>
                <Lock className="h-4 w-4 mr-2" />Lawyer Only
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleVisibilityChange(doc.id, 'lawyer-agent')}>
                <Shield className="h-4 w-4 mr-2" />Lawyer & Agent
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleVisibilityChange(doc.id, 'all-parties')}>
                <Users className="h-4 w-4 mr-2" />All Parties
              </DropdownMenuItem>
              {canDeleteDocument(doc, user?.id) ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive" onClick={() => void handleDeleteDocument(doc)}>
                    <XCircle className="h-4 w-4 mr-2" />Delete Upload
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Documents</CardTitle>
            <p className="text-sm text-muted-foreground">{documents?.length || 0} documents organized by stage</p>
          </div>
          <Button onClick={() => { setUploadStageId(undefined); setUploadOpen(true); }}>
            <Upload className="h-4 w-4 mr-2" />Upload Document
          </Button>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" className="space-y-2">
            {(stages || []).map((stage) => {
              const stageDocs = getDocsForStage(stage.id);
              const status = stage.status || 'not-started';
              const badgeClass = status === 'completed' ? 'bg-primary/10 text-primary' : status === 'in-progress' ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground';
              return (
                <AccordionItem key={stage.id} value={stage.id} className="border border-border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <Folder className="h-5 w-5 text-primary" />
                      <span className="font-medium">{stage.stage_name}</span>
                      <Badge className={badgeClass}>{status.replace("-", " ")}</Badge>
                      <span className="text-sm text-muted-foreground">({stageDocs.length} files)</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {stageDocs.length === 0 ? (
                      <div className="py-8 text-center">
                        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No documents uploaded yet</p>
                        <Button variant="outline" size="sm" className="mt-3" onClick={() => { setUploadStageId(stage.id); setUploadOpen(true); }}>
                          <Plus className="h-4 w-4 mr-2" />Upload Document
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2 pt-2">
                        {stageDocs.map(renderDocRow)}
                        <Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => { setUploadStageId(stage.id); setUploadOpen(true); }}>
                          <Plus className="h-4 w-4 mr-1" />Add Document
                        </Button>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}

            {unstagedDocs.length > 0 && (
              <AccordionItem value="unstaged" className="border border-border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Folder className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">General Documents</span>
                    <span className="text-sm text-muted-foreground">({unstagedDocs.length} files)</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2 pt-2">{unstagedDocs.map(renderDocRow)}</div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>

          {(documents || []).filter(d => d.status === 'signed' || d.status === 'verified').map(doc => (
            <SignatureVerification key={doc.id} documentId={doc.id} />
          ))}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <DocumentUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        defaultMatterId={matterId}
        defaultStageId={uploadStageId}
      />

      {signingDoc && (
        <SignatureDialog
          open={!!signingDoc}
          documentId={signingDoc.id}
          documentName={signingDoc.name}
          onClose={() => setSigningDoc(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['documents'] });
            queryClient.invalidateQueries({ queryKey: ['signatures'] });
            setSigningDoc(null);
          }}
        />
      )}

      {/* Reject Dialog */}
      <Dialog open={!!rejectDoc} onOpenChange={() => setRejectDoc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Document</DialogTitle>
            <DialogDescription>Provide a reason for rejecting this document.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input
              placeholder="Reason for rejection..."
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDoc(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
